import type { ReactNode } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { PlanViewMode } from "./plan-utils"

import { ArrowLeft, Download, FileText, LayoutGrid, Sparkles } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

import { SegmentAiDisclaimer } from "../segment-ai-disclaimer"

/** Shared plan chrome: back nav, title + meta, Document/Canvas toggle, Export stub, disclaimer. */
export function PlanShell({
  backLabel,
  onBack,
  title,
  kindLabel,
  metaSlot,
  mode,
  onModeChange,
  canEdit,
  isGenerating,
  onRegenerate,
  children,
}: {
  backLabel: string
  onBack: () => void
  title: string
  kindLabel: string
  metaSlot?: ReactNode
  mode: PlanViewMode
  onModeChange: (mode: PlanViewMode) => void
  canEdit: boolean
  isGenerating?: boolean
  onRegenerate?: () => void
  children: ReactNode
}) {
  const { t } = useTranslation()
  return (
    <section className="space-y-5">
      <header className="space-y-3">
        <button
          className="text-muted-foreground hover:text-foreground inline-flex max-w-full items-center gap-1 text-xs font-medium transition"
          type="button"
          onClick={onBack}
        >
          <ArrowLeft className="size-3.5 shrink-0" />
          <span className="max-w-[60ch] truncate">{backLabel}</span>
        </button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-primary text-xs font-semibold tracking-[0.12em] uppercase">
              {kindLabel}
            </p>
            <h2 className="text-foreground mt-1 text-2xl font-semibold">{title}</h2>
            {metaSlot ? <div className="mt-2">{metaSlot}</div> : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div
              className="bg-muted text-muted-foreground inline-flex rounded-md p-0.5 text-xs font-medium shadow-inner"
              data-testid="sa-plan-view-toggle"
            >
              <button
                aria-pressed={mode === "document"}
                data-testid="sa-plan-view-document"
                type="button"
                className={cn(
                  "btn-spring inline-flex h-8 items-center gap-1.5 rounded px-3 transition",
                  mode === "document"
                    ? "bg-background text-foreground ring-border/70 shadow-sm ring-1"
                    : "hover:text-foreground"
                )}
                onClick={() => onModeChange("document")}
              >
                <FileText className="size-3.5" />
                {t("segmentAnalysis.plan.documentView")}
              </button>
              <button
                aria-pressed={mode === "canvas"}
                data-testid="sa-plan-view-canvas"
                type="button"
                className={cn(
                  "btn-spring inline-flex h-8 items-center gap-1.5 rounded px-3 transition",
                  mode === "canvas"
                    ? "bg-background text-foreground ring-border/70 shadow-sm ring-1"
                    : "hover:text-foreground"
                )}
                onClick={() => onModeChange("canvas")}
              >
                <LayoutGrid className="size-3.5" />
                {t("segmentAnalysis.plan.canvasView")}
              </button>
            </div>
            {canEdit && onRegenerate ? (
              <Button
                data-testid="sa-plan-regenerate"
                disabled={isGenerating}
                size="sm"
                variant="outline"
                onClick={onRegenerate}
              >
                <Sparkles className="size-4" />
                {isGenerating
                  ? t("segmentAnalysis.plan.generating")
                  : t("segmentAnalysis.plan.regenerate")}
              </Button>
            ) : null}
            <Button
              data-testid="sa-plan-export"
              size="sm"
              variant="ghost"
              onClick={() => toast(t("segmentAnalysis.plan.exportComingSoon"))}
            >
              <Download className="size-4" />
              {t("segmentAnalysis.plan.export")}
            </Button>
          </div>
        </div>
        <SegmentAiDisclaimer />
      </header>
      {children}
    </section>
  )
}
